import { useEffect, useRef, useState } from 'react';
import { INSTAGRAM_HANDLE, INSTAGRAM_POST_URLS, INSTAGRAM_PROFILE_URL } from '../config/instagram';

declare global {
  interface Window {
    instgrm?: { Embeds: { process: () => void } };
  }
}

let scriptPromise: Promise<void> | null = null;

function loadInstagramEmbedScript(): Promise<void> {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[src="https://www.instagram.com/embed.js"]');
    if (existing) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://www.instagram.com/embed.js';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Instagram embed script failed to load'));
    document.body.appendChild(script);
  });
  return scriptPromise;
}

/**
 * Renders official Instagram <blockquote> embeds, but only once the panel actually
 * scrolls into view — so a slow or blocked instagram.com request never delays the
 * check-in form or any other above-the-fold content (spec §46, §53).
 */
export function InstagramPanel({ compact = false }: { compact?: boolean }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (INSTAGRAM_POST_URLS.length === 0) return;
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!shouldLoad) return;
    loadInstagramEmbedScript()
      .then(() => window.instgrm?.Embeds.process())
      .catch(() => setFailed(true));
  }, [shouldLoad]);

  return (
    <div ref={containerRef} className={compact ? 'space-y-3' : 'space-y-4'}>
      <div className="flex items-center justify-between">
        <h2 className="text-xl">From Instagram</h2>
        <a
          href={INSTAGRAM_PROFILE_URL}
          target="_blank"
          rel="noreferrer noopener"
          className="text-sm text-primary hover:underline"
        >
          {INSTAGRAM_HANDLE}
        </a>
      </div>

      {INSTAGRAM_POST_URLS.length === 0 || failed ? (
        <a
          href={INSTAGRAM_PROFILE_URL}
          target="_blank"
          rel="noreferrer noopener"
          className="card flex items-center justify-center py-10 text-primary hover:border-primary/60"
        >
          See our latest work on Instagram →
        </a>
      ) : (
        <div className={compact ? 'grid gap-4' : 'grid gap-4 sm:grid-cols-2'}>
          {INSTAGRAM_POST_URLS.map((url) => (
            <blockquote
              key={url}
              className="instagram-media"
              data-instgrm-permalink={url}
              data-instgrm-version="14"
              style={{ background: '#161513', border: 0, borderRadius: 10, minWidth: 240 }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
